import React, { Suspense } from 'react';
import { useRoutes, Navigate } from 'react-router-dom';
// @ts-ignore: CSS module type declarations are not available for this side-effect import
import 'react-notifications/lib/notifications.css';

// layouts
import DefaultLayout from '../layouts/Default';
import VerticalLayout from '../layouts/Vertical';
import HorizontalLayout from '../layouts/Horizontal/';

// components
import PrivateRoute from './PrivateRoute';
import Root from './Root';

// constants
import { LayoutTypes } from '../constants';

// hooks
import { useRedux, useUser } from '../hooks';

// route menu imports
import {
    Login,
    Register,
    Confirm,
    ForgetPassword,
    LockScreen,
    Logout,
    DashBoard1,
    Error404,
    Error500,
    Areas,
    Landmark,
    Customer,
    Product,
    Allotment,
    ExpenseCategory,
    Designation,
    Employee,
    Branch,
    Sales,
    Collections,
    Return,
    Expenses,
    Outstanding,
    Settings,
    Draft,
    AreawiseReport,
    DaywiseReport,
    ProductwiseReport,
    UserRights,
    Tally,
    FeatherIcons,
    MDIIcons,
} from './Route_Menu';

type LoadComponentProps = {
    component: React.ComponentType<any>; // Use `any` if the component's props are unknown
    permission?: string[]; // Optional permission prop
};

// Loading component for Suspense fallback
const loading = () => <div className="loading">Loading...</div>;

// Lazy component loader with Suspense
const LoadComponent: React.FC<LoadComponentProps> = ({ component: Component, permission = [] }) => {
    const [loggedInUser] = useUser(); // Get the logged-in user data
    const userRights = JSON.parse(loggedInUser?.userRights || '{}'); // Retrieve user rights
    const userRole = loggedInUser?.user?.role; // Retrieve user role
    if (userRole === 'Employee' && permission.length > 0) {
        const hasPermission = permission.some((perm) => userRights[perm]);
        if (!hasPermission) {
            return <Navigate to="/access-denied" />;
        }
    }
    return (
        <Suspense fallback={loading()}>
            <Component permission={permission} userRole={userRole} userRights={userRights} />
        </Suspense>
    );
};

const AllRoutes = () => {
    const { appSelector } = useRedux();

    // Get layout type from Redux
    const { layout } = appSelector((state) => ({
        layout: state.Layout,
    }));

    // Dynamically choose the layout based on layout type
    const getLayout = () => {
        switch (layout.layoutType) {
            case LayoutTypes.LAYOUT_HORIZONTAL:
                return HorizontalLayout;
            default:
                return VerticalLayout;
        }
    };

    const Layout = getLayout();

    // Define routes
    return useRoutes([
        {
            path: '/',
            element: <Root />,
        },
        {
            // Public routes
            path: '/',
            element: <DefaultLayout />,
            children: [
                {
                    path: 'auth',
                    children: [
                        { path: 'login', element: <LoadComponent component={Login} /> },
                        { path: 'register', element: <LoadComponent component={Register} /> },
                        { path: 'confirm', element: <LoadComponent component={Confirm} /> },
                        { path: 'forget-password', element: <LoadComponent component={ForgetPassword} /> },
                        { path: 'lock-screen', element: <LoadComponent component={LockScreen} /> },
                        { path: 'logout', element: <LoadComponent component={Logout} /> },
                    ],
                },
                { path: 'error-404', element: <LoadComponent component={Error404} /> },
                { path: 'error-500', element: <LoadComponent component={Error500} /> },
            ],
        },
        {
            // Auth-protected routes
            path: '/',
            element: <PrivateRoute roles={['SuperAdmin']} component={Layout} />,
            children: [
                {
                    path: 'branch',
                    element: (
                        <LoadComponent component={Branch} permission={['branch_ins', 'branch_upd', 'branch_del']} />
                    ),
                },
            ],
        },
        {
            // Auth-protected routes
            path: '/',
            element: <PrivateRoute roles={['Admin', 'Employee', 'SuperAdmin']} component={Layout} />,
            children: [
                {
                    path: 'dashboard',
                    element: (
                        <LoadComponent
                            component={DashBoard1}
                            permission={[
                                'order_ins',
                                'order_upd',
                                'order_del',
                                'return_ins',
                                'return_upd',
                                'return_del',
                            ]}
                        />
                    ),
                },
                {
                    path: 'outstanding',
                    element: <LoadComponent component={Outstanding} permission={['outstanding_view']} />,
                },
                {
                    path: 'view',
                    children: [
                        {
                            path: 'areas',
                            element: (
                                <LoadComponent
                                    component={Areas}
                                    permission={['master_ins', 'master_upd', 'master_del']}
                                />
                            ),
                        },
                        {
                            path: 'landmark',
                            element: (
                                <LoadComponent
                                    component={Landmark}
                                    permission={['master_ins', 'master_upd', 'master_del']}
                                />
                            ),
                        },
                        {
                            path: 'customer',
                            element: (
                                <LoadComponent
                                    component={Customer}
                                    permission={['master_ins', 'master_upd', 'master_del']}
                                />
                            ),
                        },
                        {
                            path: 'product',
                            element: (
                                <LoadComponent
                                    component={Product}
                                    permission={['master_ins', 'master_upd', 'master_del']}
                                />
                            ),
                        },
                        {
                            path: 'allotment',
                            element: (
                                <LoadComponent
                                    component={Allotment}
                                    permission={['master_ins', 'master_upd', 'master_del']}
                                />
                            ),
                        },
                        {
                            path: 'expensecategory',
                            element: (
                                <LoadComponent
                                    component={ExpenseCategory}
                                    permission={['master_ins', 'master_upd', 'master_del']}
                                />
                            ),
                        },
                        {
                            path: 'designation',
                            element: (
                                <LoadComponent
                                    component={Designation}
                                    permission={['master_ins', 'master_upd', 'master_del']}
                                />
                            ),
                        },
                        {
                            path: 'employee',
                            element: (
                                <LoadComponent component={Employee} permission={['user_ins', 'user_upd', 'user_del']} />
                            ),
                        },
                        {
                            path: 'userrights',
                            element: (
                                <LoadComponent
                                    component={UserRights}
                                    permission={['userright_view', 'userright_ins']}
                                />
                            ),
                        },
                    ],
                },
                {
                    path: 'sales',
                    element: <LoadComponent component={Sales} permission={['order_ins', 'order_upd', 'order_del']} />,
                },
                {
                    path: 'collections',
                    element: (
                        <LoadComponent
                            component={Collections}
                            permission={['collection_ins', 'collection_upd', 'collection_del']}
                        />
                    ),
                },
                {
                    path: 'return',
                    element: (
                        <LoadComponent component={Return} permission={['return_ins', 'return_upd', 'return_del']} />
                    ),
                },
                {
                    path: 'expenses',
                    element: (
                        <LoadComponent
                            component={Expenses}
                            permission={['expenses_ins', 'expenses_upd', 'expenses_del']}
                        />
                    ),
                },
                {
                    path: 'draft',
                    element: <LoadComponent component={Draft} permission={['draft_view', 'draft_del']} />,
                },
                {
                    path: 'settings',
                    element: <LoadComponent component={Settings} permission={['settings_upd', 'settings_view']} />,
                },
                {
                    path: 'tally',
                    element: (
                        <LoadComponent
                            component={Tally}
                            permission={['tally_view','tally_ins', 'tally_upd', 'tally_del']}
                        />
                    ),
                },
                {
                    path: 'report',
                    children: [
                        {
                            path: 'areawisereport',
                            element: <LoadComponent component={AreawiseReport} permission={['reports_view']} />,
                        },
                        {
                            path: 'daywisereport',
                            element: <LoadComponent component={DaywiseReport} permission={['reports_view']} />,
                        },
                        {
                            path: 'productwisereport',
                            element: <LoadComponent component={ProductwiseReport} permission={['reports_view']} />,
                        }
                    ],
                },
                {
                    path: 'access-denied',
                    element: <LoadComponent component={Error500} />,
                },
                {
                    path: 'icons',
                    element: <LoadComponent component={MDIIcons} />,
                },
            ],
        },
    ]);
};

export { AllRoutes };
